import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { FileText, ArrowLeft, CheckCircle2, FileCheck, Calendar, Home, LogOut, Heart, Lock, Eye, Languages } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { ContextualServices } from '../components/ContextualServices';
import { translateScheme, translateText } from '../services/translationService';
import axios from 'axios';
import useSeo from '../hooks/useSeo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sector translation mapping
const SECTOR_TRANSLATION_KEYS = {
  'Agriculture': 'sectorAgriculture',
  'Health': 'sectorHealth',
  'Housing': 'sectorHousing',
  'Education': 'sectorEducation',
  'Employment': 'sectorEmployment',
  'Business & Entrepreneurship': 'sectorBusiness',
  'Women & Child': 'sectorWomenChild',
  'Social Welfare': 'sectorSocialWelfare',
  'Financial Inclusion': 'sectorFinancial',
  'Rural Development': 'sectorRural',
  'Urban Development': 'sectorUrban',
  'Skill Development': 'sectorSkill',
  'Skills & Employment': 'sectorSkillsEmployment',
  'Food Security': 'sectorFood',
  'Insurance': 'sectorInsurance',
  'Pension': 'sectorPension',
};

// State translation mapping
const STATE_TRANSLATION_KEYS = {
  'Central': 'central',
  'Andhra Pradesh': 'andhrapradesh',
  'Telangana': 'telangana',
  'Karnataka': 'karnataka'
};

const SchemeDetails = () => {
  const { slug: slugOrId } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { t, language } = useLanguage();
  const [scheme, setScheme] = useState(null);
  // Canonical URL must be the slug-based form (set after fetch). Until then,
  // canonical points at whatever is in the address bar — which is fine because
  // we always navigate-replace UUID URLs to the slug equivalent below.
  useSeo({
    canonical: scheme?.slug
      ? `https://knowmyschemes.com/schemes/${scheme.slug}`
      : `https://knowmyschemes.com/schemes/${slugOrId}`,
  });
  const [translatedScheme, setTranslatedScheme] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [userSubscribed, setUserSubscribed] = useState(false);
  const [viewsRemaining, setViewsRemaining] = useState(2);
  const [viewsExhausted, setViewsExhausted] = useState(false);
  const [viewTracked, setViewTracked] = useState(false);
  const hasTrackedView = React.useRef(false); // Prevent double tracking in Strict Mode

  // Translate scheme when language changes
  useEffect(() => {
    const doTranslation = async () => {
      if (!scheme || language === 'en') {
        setTranslatedScheme(null);
        return;
      }
      
      setIsTranslating(true);
      try {
        const translated = await translateScheme(scheme.id, language);
        if (translated) {
          setTranslatedScheme(translated);
        }
      } catch (error) {
        console.error('Translation error:', error);
      } finally {
        setIsTranslating(false);
      }
    };
    
    doTranslation();
  }, [scheme, language]);

  // Helper function to get translated scheme field
  const getTranslatedField = (scheme, field) => {
    if (!scheme) return '';
    
    // If we have live translation, use it
    if (translatedScheme && language !== 'en') {
      if (field === 'name' && translatedScheme.name) return translatedScheme.name;
      if (field === 'description' && translatedScheme.description) return translatedScheme.description;
      if (field === 'benefits' && translatedScheme.benefits?.length) return translatedScheme.benefits;
      if (field === 'eligibility' && translatedScheme.eligibility?.length) return translatedScheme.eligibility;
      if (field === 'documents' && translatedScheme.documents?.length) return translatedScheme.documents;
      if (field === 'how_to_apply' && translatedScheme.how_to_apply?.length) return translatedScheme.how_to_apply;
    }
    
    // If language is English or no translations exist, return original
    if (language === 'en' || !scheme.translations) {
      return scheme[field];
    }
    
    // Try to get pre-existing translated field for current language
    const langTranslations = scheme.translations[language];
    if (langTranslations && langTranslations[field]) {
      return langTranslations[field];
    }
    
    // Fallback to original
    return scheme[field];
  };

  // Helper function to translate sector names
  const translateSector = (sector) => {
    const key = SECTOR_TRANSLATION_KEYS[sector];
    return key ? t(key) || sector : sector;
  };

  // Helper function to translate state names
  const translateState = (state) => {
    const key = STATE_TRANSLATION_KEYS[state];
    return key ? t(key) || state : state;
  };

  // Helper function to translate scheme type
  const translateType = (type) => {
    if (type?.toLowerCase() === 'central') return t('central');
    if (type?.toLowerCase() === 'state') return t('stateType');
    return type;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if scheme is saved
  useEffect(() => {
    const checkSaved = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/saved-schemes/ids`);
        // Saved IDs are scheme UUIDs, not slugs — match against the resolved id
        const schemeId = scheme?.id || slugOrId;
        setIsSaved(response.data.includes(schemeId));
      } catch (error) {
        console.error('Error checking saved status:', error);
      }
    };
    checkSaved();
  }, [slugOrId, scheme?.id]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/subscription/status?user_id=${user.id}`);
        setUserSubscribed(response.data.is_subscribed);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };
    checkSubscription();
  }, [user]);

  // Toggle save
  const handleToggleSave = async () => {
    const schemeId = scheme?.id || slugOrId;
    try {
      if (isSaved) {
        await axios.delete(`${BACKEND_URL}/api/saved-schemes/unsave/${schemeId}`);
        setIsSaved(false);
      } else {
        await axios.post(`${BACKEND_URL}/api/saved-schemes/save`, { scheme_id: schemeId });
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  // Track view for premium schemes (only for subscribed users)
  const trackView = async () => {
    if (viewTracked) return;
    const schemeId = scheme?.id || slugOrId;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/schemes/track-view`,
        { scheme_id: schemeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setViewsRemaining(response.data.remaining_views);
      setViewsExhausted(response.data.is_blocked);
      setViewTracked(true);
      
      return response.data;
    } catch (error) {
      console.error('Error tracking view:', error);
      return null;
    }
  };

  useEffect(() => {
    // Reset tracking ref when scheme ID changes
    hasTrackedView.current = false;
    
    const fetchScheme = async () => {
      try {
        setLoading(true);
        
        // First fetch scheme details directly (backend accepts both slug and UUID)
        const detailResponse = await axios.get(`${BACKEND_URL}/api/schemes/${slugOrId}`);
        const schemeData = detailResponse.data;
        
        if (!schemeData) {
          setError('Scheme not found');
          setLoading(false);
          return;
        }

        // SEO 301-equivalent: if the URL param is a UUID (or any non-slug
        // identifier) and the scheme has a real slug, replace history with
        // the canonical slug URL. This funnels link-equity to the slug URL
        // for Google indexing while preserving any existing UUID links.
        if (
          schemeData.slug &&
          schemeData.slug !== slugOrId &&
          slugOrId !== schemeData.id // already the canonical
        ) {
          navigate(`/schemes/${schemeData.slug}`, { replace: true });
          return; // useEffect will re-run with the new slug; stop here
        }
        if (schemeData.slug && slugOrId === schemeData.id) {
          // User came in via legacy /schemes/<uuid>; redirect to slug URL
          navigate(`/schemes/${schemeData.slug}`, { replace: true });
          return;
        }
        
        // Check if scheme is premium
        const isPremiumScheme = schemeData.is_premium === true;
        setIsPremium(isPremiumScheme);
        
        if (isPremiumScheme) {
          // For premium schemes, check subscription status first
          let isSubscribed = false;
          if (user) {
            try {
              const subResponse = await axios.get(`${BACKEND_URL}/api/subscription/status?user_id=${user.id}`);
              isSubscribed = subResponse.data.is_subscribed;
              setUserSubscribed(isSubscribed);
            } catch (e) {
              console.error('Error checking subscription:', e);
            }
          }
          
          if (!isSubscribed) {
            // Not subscribed - show locked
            setIsLocked(true);
            setScheme(schemeData);
          } else {
            // Subscribed - check view status and track view
            try {
              const token = localStorage.getItem('token');
              
              // First check current view status (uses real UUID id)
              const viewStatusResponse = await axios.get(
                `${BACKEND_URL}/api/schemes/view-status/${schemeData.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              const currentViews = viewStatusResponse.data.view_count || 0;
              const isAlreadyBlocked = viewStatusResponse.data.is_blocked;
              
              if (isAlreadyBlocked) {
                // Already exhausted views (viewed 2 times before) - show locked
                setViewsExhausted(true);
                setViewsRemaining(0);
                setIsLocked(true);
                setScheme(schemeData);
              } else if (!hasTrackedView.current) {
                // Has views remaining and haven't tracked yet - track this view
                hasTrackedView.current = true; // Prevent double tracking
                
                const trackResponse = await axios.post(
                  `${BACKEND_URL}/api/schemes/track-view`,
                  { scheme_id: schemeData.id },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                // Show the scheme details (this is a valid view)
                setViewsRemaining(trackResponse.data.remaining_views);
                setViewsExhausted(false);
                setViewTracked(true);
                setIsLocked(false);
                setScheme(schemeData);
              } else {
                // Already tracked in this render cycle - just show scheme
                setIsLocked(false);
                setScheme(schemeData);
              }
            } catch (e) {
              console.error('Error with view tracking:', e);
              // On error, show the scheme anyway
              setScheme(schemeData);
            }
          }
        } else {
          // Free scheme - show full details
          setIsLocked(false);
          setScheme(schemeData);
        }
      } catch (error) {
        console.error('Error fetching scheme:', error);
        setError('Scheme not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchScheme();
  }, [slugOrId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-base sm:text-lg">{t('loadingSchemes')}</p>
        </div>
      </div>
    );
  }

  if (error || !scheme) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-4">
        <Card className="p-6 sm:p-8 text-center max-w-md border-none shadow-xl">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">{t('schemeNotFound')}</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">{t('schemeNotFoundDesc')}</p>
          <Button onClick={() => navigate(-1)} className="bg-gradient-to-r from-green-600 to-green-700 text-white">
            {t('backToSchemes')}
          </Button>
        </Card>
      </div>
    );
  }

  // Show locked scheme message - different for views exhausted vs not subscribed
  if (isLocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-100 flex items-center justify-center px-4">
        <Card className="p-6 sm:p-8 text-center max-w-md border-none shadow-xl">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-purple-600" />
          </div>
          {viewsExhausted ? (
            <>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-purple-800">
                {t('viewsExhausted') || 'Views Exhausted'}
              </h2>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {t('viewsExhaustedDesc') || 'You have used all your views for this premium scheme. Contact our team for assistance with your application.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/schemes')} 
                  variant="outline"
                  className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {t('backToSchemes')}
                </Button>
                <Button 
                  onClick={() => window.open('tel:+919876543210')} 
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800"
                >
                  {t('contactTeam') || 'Contact Team'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-purple-800">{t('premiumScheme')}</h2>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {t('premiumSchemeAccessDesc') || 'This is a premium scheme. Subscribe to get expert assistance with your application.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/schemes')} 
                  variant="outline"
                  className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {t('backToSchemes')}
                </Button>
                <Button 
                  onClick={() => navigate('/subscription')} 
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700"
                >
                  {t('viewPlans')}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.jpeg" alt="Logo" className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl shadow-lg object-cover" />
            <div className="hidden sm:block">
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                {t('appName')}
              </h1>
              <p className="text-xs text-gray-600">AP, TS & KA</p>
            </div>
          </div>
          <div className="flex gap-1.5 sm:gap-2">
            <LanguageSwitcher variant="compact" />
            <Button 
              onClick={() => navigate(-1)} 
              variant="outline" 
              size="sm"
              className="border-green-600 text-green-700 hover:bg-green-50 px-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('back')}</span>
            </Button>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline" 
              size="sm"
              className="border-green-600 text-green-700 hover:bg-green-50 px-2 sm:px-3"
            >
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('home')}</span>
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 px-2 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-5xl">
        {/* Premium Scheme Views Remaining Banner */}
        {isPremium && userSubscribed && !viewsExhausted && (
          <Card className="p-3 sm:p-4 mb-4 sm:mb-6 border-none shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                <span className="font-medium text-sm sm:text-base">
                  {t('premiumScheme')} - {viewsRemaining} {t('viewsRemaining') || 'view(s) remaining'}
                </span>
              </div>
              {viewsRemaining === 0 && (
                <Badge className="bg-white/20 text-white text-xs">
                  {t('lastView') || 'Last View'}
                </Badge>
              )}
            </div>
          </Card>
        )}

        {/* Scheme Header */}
        <Card className="p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border-none shadow-xl bg-white">
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Badge 
              className={`text-xs sm:text-sm ${
                scheme.state === 'Andhra Pradesh' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                  : scheme.state === 'Telangana'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
              } shadow-md`}
            >
              {translateState(scheme.state)}
            </Badge>
            <Badge 
              className={`text-xs sm:text-sm ${
                scheme.type === 'state' 
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
              } shadow-md`}
            >
              {translateType(scheme.type)}
            </Badge>
            <Badge variant="outline" className="text-xs sm:text-sm border-green-600 text-green-700">
              {translateSector(scheme.sector)}
            </Badge>
            {/* Save Button */}
            <button
              onClick={handleToggleSave}
              className={`ml-auto p-2 rounded-full transition-all duration-200 ${
                isSaved
                  ? 'bg-pink-100 text-pink-600 hover:bg-pink-200'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-pink-500'
              }`}
              title={isSaved ? t('removeFromSaved') : t('saved')}
            >
              <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
            </button>
          </div>

          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold mb-3 sm:mb-4 leading-tight bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
            {getTranslatedField(scheme, 'name')}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">{getTranslatedField(scheme, 'description')}</p>
        </Card>

        {/* Eligibility Criteria */}
        <Card className="p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border-none shadow-xl bg-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">{t('eligibilityCriteria')}</h2>
          </div>
          <Separator className="mb-3 sm:mb-4 bg-purple-100" />
          <div className="space-y-2 sm:space-y-3">
            {(getTranslatedField(scheme, 'eligibility') || []).map((criteria, index) => (
              <div key={index} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-700 mt-0.5 flex-shrink-0" />
                <p className="text-gray-800 text-sm sm:text-base">{criteria}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-purple-800 text-sm sm:text-base">{t('casteEligibility')}:</span>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {scheme.caste.map((c, idx) => (
                  <Badge key={idx} className="bg-gradient-to-r from-green-600 to-green-700 text-white shadow-sm text-xs sm:text-sm">
                    {c === 'All' ? t('all') : c}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Benefits */}
        <Card className="p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border-none shadow-xl bg-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">{t('benefits')}</h2>
          </div>
          <Separator className="mb-3 sm:mb-4 bg-blue-100" />
          <div className="space-y-2 sm:space-y-3">
            {(getTranslatedField(scheme, 'benefits') || []).map((benefit, index) => (
              <div key={index} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-800 font-medium text-sm sm:text-base">{benefit}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Required Documents */}
        <Card className="p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border-none shadow-xl bg-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
              <FileCheck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">{t('requiredDocuments')}</h2>
          </div>
          <Separator className="mb-3 sm:mb-4 bg-amber-100" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {(getTranslatedField(scheme, 'documents') || []).map((doc, index) => (
              <div key={index} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors border border-amber-200">
                <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                <p className="text-gray-800 font-medium text-sm sm:text-base">{doc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Application Process */}
        <Card className="p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border-none shadow-xl bg-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">{t('howToApply')}</h2>
          </div>
          <Separator className="mb-3 sm:mb-4 bg-purple-100" />
          <p className="text-gray-800 mb-4 sm:mb-6 leading-relaxed bg-slate-50 p-3 sm:p-4 rounded-lg text-sm sm:text-base">{getTranslatedField(scheme, 'howToApply')}</p>
          
          <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-700 flex-shrink-0" />
            <div className="text-sm sm:text-base">
              <span className="font-semibold text-gray-900">{t('deadline')}: </span>
              <span className="text-purple-800">{getTranslatedField(scheme, 'deadline')}</span>
            </div>
          </div>
        </Card>

        {/* Contextual KMS Services — partner/service cards matched to this scheme */}
        <div className="mb-4 sm:mb-6">
          <ContextualServices
            text={`${scheme.sector || ''} ${scheme.name || ''} ${scheme.description || ''} ${(scheme.benefits || []).join(' ')}`}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center sm:justify-start">
          <Button 
            variant="outline"
            className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 border-2 border-green-600 text-green-700 hover:bg-green-50 transition-all duration-300"
            onClick={() => navigate('/schemes')}
            data-testid="browse-more-schemes-btn"
          >
            {t('browseMoreSchemes')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SchemeDetails;

